import { createInertiaApp, router } from '@inertiajs/react'
import { createElement, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { installStandaloneRecovery } from '@/lib/standalone-recovery'
import { captureFirstTouch } from '@/lib/attribution'
import { trackPageview } from '@/lib/analytics'

type ResolvedComponent = {
  default: ReactNode
  layout?: (page: ReactNode) => ReactNode
}

// Recover iOS Home Screen (standalone PWA) sessions from a dead/suspended
// WebKit context. Client-only — never imported by the SSR entrypoint.
installStandaloneRecovery()

// Record the landing page + referrer before React mounts, so the first page of
// the session is what gets credited — not whichever page happens to hold a
// form. Client-only; the SSR entrypoint must never call this.
captureFirstTouch()

// Count in-site navigations in Fathom. Its script fires one pageview on load
// and nothing after, so without this every Inertia click is invisible and the
// funnel looks like it stops at the landing page. Skip the first `navigate`,
// which fires for the page the script already counted.
let firstNavigate = true
router.on('navigate', () => {
  if (firstNavigate) {
    firstNavigate = false
    return
  }
  trackPageview()
})

createInertiaApp({
  resolve: (name) => {
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }
    return page
  },

  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(
        createElement(ErrorBoundary, null, createElement(App, props)),
      )
    } else {
      console.error(
        'Missing root element. Move `vite_typescript_tag "inertia"` into an Inertia-specific layout.',
      )
    }
  },
})
